import { Campaign, ErrorResponse, ServiceResponse } from '@bzbs/react-api-client';
import { create } from 'zustand';
import { useBuzzebeesAppStore } from './buzzebeess-app.store';
import { useLocaleStore } from './locale.store';

export interface CampaignsConfig {
  itemsPerPage?: number;
  defaultCampaignConfig?: string;
}

export interface CampaignsOptions {
  keyword?: string;
  startDate?: string;
  sponsorId?: string;
  maxPoints?: string;
  minPoints?: string;
  minPrice?: string;
  maxPrice?: string;
  sortBy?: string;
  center?: string;
  hashTags?: string;
  locationAgencyId?: string;
  campaignservice?: boolean;
  mode?: string | 'hot' | 'all' | 'bzbs' | 'sponsor' | 'draw' | 'free' | 'deal' | 'buy' | 'ads' | 'full' | 'cart';
  [key: string]: unknown;
}

export type CampaignsState = {
  data: Campaign[];
  isLoading: boolean;
  hasMore: boolean;
  error: ErrorResponse | null;
  currentPage: number;
  categoryId: string | null;
  options?: CampaignsOptions;
  config: CampaignsConfig;
};

export type CampaignsActions = {
  fetchData: (
    categoryId?: string,
    options?: CampaignsOptions
  ) => Promise<ServiceResponse<Campaign[]>>;
  loadMore: () => Promise<ServiceResponse<Campaign[]>>;
  clear: () => void;
  updateConfig: (config: Partial<CampaignsConfig>) => void;
};

export type CampaignsStore = CampaignsState & CampaignsActions;

export const createCampaignsStore = (initialConfig: CampaignsConfig) => {
  return create<CampaignsStore>((set, get) => ({
    data: [],
    isLoading: false,
    hasMore: true,
    error: null,
    currentPage: 0,
    categoryId: null,
    config: {
      ...initialConfig,
      itemsPerPage: initialConfig.itemsPerPage || 25,
    },
    options: {},

    updateConfig: (newConfig: Partial<CampaignsConfig>) => {
      set((state) => ({
        config: { ...state.config, ...newConfig },
      }));
    },

    fetchData: async (categoryId?: string, options?: CampaignsOptions) => {
      const { config } = get();
      const localeId = useLocaleStore.getState().localeId;
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService || !config.defaultCampaignConfig) {
        return { type: 'client-error', message: 'Service not configured or missing config' };
      }

      set({
        currentPage: 0,
        data: [],
        hasMore: true,
        error: null,
        isLoading: true,
        categoryId: categoryId || null,
        options: options || undefined,
      });

      const response = await bzbsService.campaignApi.campaigns({
        config: config.defaultCampaignConfig,
        deviceLocale: localeId,
        top: config.itemsPerPage,
        skip: 0,
        byConfig: true,
        cat: categoryId,
        ...options,
      });

      if (response.type === 'success') {
        set({
          data: response.model,
          hasMore: response.model.length === config.itemsPerPage,
          isLoading: false,
          error: null,
          currentPage: get().currentPage + 1,
        });
      } else {
        set({ error: response, isLoading: false, hasMore: false });
      }

      return response;
    },

    loadMore: async () => {
      const { config, data, hasMore, currentPage, categoryId, options } = get();
      const localeId = useLocaleStore.getState().localeId;
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService || !config.defaultCampaignConfig || !hasMore) {
        return { type: 'client-error', message: 'Service not configured or missing config' };
      }

      if (!hasMore) {
        return { type: 'client-error', message: 'No more data available' };
      }

      set({ isLoading: true, error: null });
      const response = await bzbsService.campaignApi.campaigns({
        config: config.defaultCampaignConfig,
        deviceLocale: localeId,
        top: config.itemsPerPage,
        skip: currentPage * (config.itemsPerPage || 25),
        byConfig: true,
        cat: categoryId || undefined,
        ...options,
      });

      if (response.type === 'success') {
        set({
          data: [...data, ...response.model],
          hasMore: response.model.length === config.itemsPerPage,
          currentPage: currentPage + 1,
          isLoading: false,
          error: null,
        });
      } else {
        set({
          error: response,
          isLoading: false,
          hasMore: false,
        });
      }

      return response;
    },

    clear: () => {
      set({
        data: [],
        isLoading: false,
        hasMore: true,
        error: null,
        currentPage: 0,
      });
    },
  }));
};
